import type { WalletAccount, WalletIcon } from "@wallet-standard/base";

import { isMainnet } from "@/env";
import { Logger } from "@/utils/logger";

// CAIP-2 chain IDs (genesis-hash prefix) used in WalletConnect namespaces
const SOLANA_MAINNET_CAIP = "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp";
const SOLANA_DEVNET_CAIP = "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1";

// Wallet Standard chain identifiers expected by @solana/wallet-adapter-base
const SOLANA_MAINNET_WS = "solana:mainnet";
const SOLANA_DEVNET_WS = "solana:devnet";

const caipChain = () => (isMainnet ? SOLANA_MAINNET_CAIP : SOLANA_DEVNET_CAIP);
const wsChain = () => (isMainnet ? SOLANA_MAINNET_WS : SOLANA_DEVNET_WS);

const WALLETCONNECT_ICON: WalletIcon =
  "data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHZpZXdCb3g9JzAgMCA0ODAgNDgwJz48cmVjdCB3aWR0aD0nNDgwJyBoZWlnaHQ9JzQ4MCcgcng9JzI0MCcgZmlsbD0nIzMzOTZGRicvPjxwYXRoIGQ9J00xNDEuMSAxODUuMmM1NC42LTUzLjUgMTQzLjItNTMuNSAxOTcuOCAwbDYuNiA2LjRhNi43IDYuNyAwIDAgMSAwIDkuN2wtMjIuNSAyMmEzLjUgMy41IDAgMCAxLTQuOSAwbC05LTguOWMtMzguMS0zNy4zLTk5LjktMzcuMy0xMzggMGwtOS43IDkuNWEzLjUgMy41IDAgMCAxLTQuOSAwbC0yMi41LTIyYTYuNyA2LjcgMCAwIDEgMC05LjdsNy4xLTdabTI0NC41IDQ1LjUgMjAgMTkuN2E2LjcgNi43IDAgMCAxIDAgOS42bC05MC4zIDg4LjRhNyA3IDAgMCAxLTkuOSAwbC02NC4xLTYyLjhhMS44IDEuOCAwIDAgMC0yLjUgMGwtNjQuMSA2Mi44YTcgNyAwIDAgMS05LjkgMGwtOTAuMy04OC40YTYuNyA2LjcgMCAwIDEgMC05LjZsMjAtMTkuN2E3IDcgMCAwIDEgOS45IDBsNjQuMSA2Mi44YTEuOCAxLjggMCAwIDAgMi41IDBsNjQuMS02Mi44YTcgNyAwIDAgMSA5LjkgMGw2NC4xIDYyLjhhMS44IDEuOCAwIDAgMCAyLjUgMGw2NC4xLTYyLjhhNyA3IDAgMCAxIDkuOSAwWicgZmlsbD0nI2ZmZicvPjwvc3ZnPg==";

type UniversalProvider = Awaited<
  ReturnType<typeof import("@walletconnect/universal-provider").default.init>
>;

let wcSolProvider: UniversalProvider | null = null;
let initPromise: Promise<UniversalProvider> | null = null;
let disconnecting: Promise<void> | null = null;
let unregisterWallet: (() => void) | null = null;

// ---------------------------------------------------------------------------
// Base58 codec (Bitcoin/Solana alphabet)
// ---------------------------------------------------------------------------

const B58_ALPHABET = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";

/** Decode a base58-encoded string (Solana alphabet) into raw bytes. */
function base58Decode(str: string): Uint8Array {
  const bytes: number[] = [0];
  for (const char of str) {
    const idx = B58_ALPHABET.indexOf(char);
    if (idx === -1) throw new Error(`Invalid base58 character: ${char}`);
    let carry = idx;
    for (let j = 0; j < bytes.length; j++) {
      const current = bytes[j] ?? 0;
      carry += current * 58;
      bytes[j] = carry & 0xff;
      carry >>= 8;
    }
    while (carry > 0) {
      bytes.push(carry & 0xff);
      carry >>= 8;
    }
  }
  for (const char of str) {
    if (char !== "1") break;
    bytes.push(0);
  }
  return new Uint8Array(bytes.reverse());
}

/** Encode raw bytes into a base58 string (Solana alphabet). */
function base58Encode(bytes: Uint8Array): string {
  const digits: number[] = [0];
  for (const byte of bytes) {
    let carry = byte;
    for (let j = 0; j < digits.length; j++) {
      const current = digits[j] ?? 0;
      carry += current * 256;
      digits[j] = carry % 58;
      carry = Math.floor(carry / 58);
    }
    while (carry > 0) {
      digits.push(carry % 58);
      carry = Math.floor(carry / 58);
    }
  }
  let result = "";
  for (const b of bytes) {
    if (b !== 0) break;
    result += "1";
  }
  for (let i = digits.length - 1; i >= 0; i--) {
    const d = digits[i] ?? 0;
    result += B58_ALPHABET[d] ?? "";
  }
  return result;
}

// ---------------------------------------------------------------------------
// Wallet Standard wallet object registered via getWallets().register()
//
// setupAutomaticSolanaWalletDerivation listens for "register" events on the
// wallet registry and, if the wallet is Solana-Wallet-Standard-compatible,
// wraps it in SolanaDerivedWallet → Aptos adapter.
// ---------------------------------------------------------------------------

type ChangeListener = (props: { accounts?: readonly WalletAccount[] }) => void;

let walletAccounts: readonly WalletAccount[] = [];
const changeListeners = new Set<ChangeListener>();

/** Fire the Wallet Standard "change" event so the derived wallet sees updated accounts. */
function notifyAccountsChanged() {
  for (const listener of changeListeners) {
    listener({ accounts: walletAccounts });
  }
}

/** Build a Wallet Standard account from a base58 Solana address. */
function buildAccount(address: string): WalletAccount {
  const chain = wsChain();
  return {
    address,
    publicKey: base58Decode(address),
    chains: [chain],
    features: ["solana:signTransaction", "solana:signMessage"],
  };
}

/** Extract the Solana address from a WC session's CAIP-10 account string. */
function extractSolanaAddress(session: {
  namespaces: Record<string, { accounts?: string[] }>;
}): string | undefined {
  const solNs = session.namespaces.solana;
  const caipAccount = solNs?.accounts?.[0];
  if (!caipAccount) return undefined;
  return caipAccount.split(":")[2];
}

const wcSolanaWallet = {
  version: "1.0.0" as const,
  name: "WalletConnect",
  icon: WALLETCONNECT_ICON,
  get chains() {
    return [wsChain()] as const;
  },
  get accounts() {
    return walletAccounts;
  },
  features: {
    "standard:connect": {
      version: "1.0.0" as const,
      connect: () => {
        return Promise.resolve({ accounts: walletAccounts });
      },
    },
    "standard:disconnect": {
      version: "1.0.0" as const,
      disconnect: () => {
        walletAccounts = [];
        notifyAccountsChanged();
        return Promise.resolve();
      },
    },
    "standard:events": {
      version: "1.0.0" as const,
      on: (event: string, listener: ChangeListener) => {
        if (event === "change") changeListeners.add(listener);
        return () => changeListeners.delete(listener);
      },
    },
    "solana:signTransaction": {
      version: "1.0.0" as const,
      supportedTransactionVersions: ["legacy" as const, 0 as const],
      signTransaction: async (
        ...inputs: ReadonlyArray<{
          account: WalletAccount;
          transaction: Uint8Array;
          chain?: string;
        }>
      ) => {
        if (!wcSolProvider) throw new Error("WalletConnect Solana not connected");
        const chain = caipChain();
        const results = [];
        for (const input of inputs) {
          const txBase64 = uint8ToBase64(input.transaction);
          const resp = await wcSolProvider.request<{ signature?: string; transaction?: string }>(
            { method: "solana_signTransaction", params: { transaction: txBase64 } },
            chain,
          );
          if (resp.transaction) {
            results.push({ signedTransaction: base64ToUint8(resp.transaction) });
          } else if (resp.signature) {
            const sig = base58Decode(resp.signature);
            const signed = embedSignature(input.transaction, sig);
            results.push({ signedTransaction: signed });
          } else {
            throw new Error("WalletConnect: unexpected signTransaction response");
          }
        }
        return results;
      },
    },
    "solana:signMessage": {
      version: "1.0.0" as const,
      signMessage: async (
        ...inputs: ReadonlyArray<{ account: WalletAccount; message: Uint8Array }>
      ) => {
        if (!wcSolProvider) throw new Error("WalletConnect Solana not connected");
        const chain = caipChain();
        const results = [];
        for (const input of inputs) {
          const msgBs58 = base58Encode(input.message);
          const resp = await wcSolProvider.request<{ signature: string }>(
            {
              method: "solana_signMessage",
              params: { message: msgBs58, pubkey: input.account.address },
            },
            chain,
          );
          const sig = base58Decode(resp.signature);
          results.push({ signedMessage: input.message, signature: sig });
        }
        return results;
      },
    },
  },
};

// ---------------------------------------------------------------------------
// Helpers for transaction signing
// ---------------------------------------------------------------------------

/** Convert a Uint8Array to a base64 string (for WC solana_signTransaction params). */
function uint8ToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

/** Convert a base64 string back to a Uint8Array (for WC signed transaction responses). */
function base64ToUint8(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * Embed a 64-byte ed25519 signature into a serialized Solana transaction.
 * Unsigned transactions have null (all-zero) 64-byte signature slots.
 */
function embedSignature(txBytes: Uint8Array, sig: Uint8Array): Uint8Array {
  const numSigs = txBytes[0];
  if (numSigs === undefined || numSigs < 1) throw new Error("Transaction has no signature slots");
  const signed = new Uint8Array(txBytes);
  const offset = numSigs < 128 ? 1 : 2;
  signed.set(sig.slice(0, 64), offset);
  return signed;
}

// ---------------------------------------------------------------------------
// Provider init / connect / restore / disconnect
// ---------------------------------------------------------------------------

/**
 * Lazily initialize (or return the cached) WalletConnect UniversalProvider.
 * Uses `customStoragePrefix: "solana"` so it gets its own Core instance and
 * IndexedDB keychain, completely isolated from the EVM provider.
 */
async function getOrInitProvider(projectId: string): Promise<UniversalProvider> {
  if (wcSolProvider) return wcSolProvider;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const { default: UniversalProvider } = await import("@walletconnect/universal-provider");

      const opts = {
        projectId,
        customStoragePrefix: "solana",
        metadata: {
          name: "Decibel",
          description: "Decibel Perpetual Futures DEX",
          url: typeof window !== "undefined" ? window.location.origin : "https://decibel.trade",
          icons: ["https://decibel.trade/favicon.ico"],
        },
      };

      let provider: UniversalProvider;
      try {
        provider = await UniversalProvider.init(opts);
      } catch {
        clearWcSolanaLocalStorage();
        provider = await UniversalProvider.init(opts);
      }

      wcSolProvider = provider;
      return provider;
    } finally {
      initPromise = null;
    }
  })();

  return initPromise;
}

/** Register `wcSolanaWallet` with the Wallet Standard registry so derived-wallet adapters discover it. */
async function registerWallet(): Promise<void> {
  if (unregisterWallet) return;

  const { getWallets } = await import("@wallet-standard/app");

  unregisterWallet = getWallets().register(wcSolanaWallet);
}

/** Remove the WalletConnect Solana wallet from the Wallet Standard registry. */
function unregister(): void {
  unregisterWallet?.();
  unregisterWallet = null;
}

/**
 * Initiate a new WalletConnect Solana session. Shows a QR code via `onDisplayUri`,
 * waits for the user to approve on their mobile wallet, then registers the
 * Wallet Standard wallet so the Aptos adapter can derive an address from it.
 */
export async function connectWcSolana(
  projectId: string,
  onDisplayUri: (uri: string) => void,
): Promise<void> {
  if (disconnecting) await disconnecting;

  const provider = await getOrInitProvider(projectId);

  const chain = caipChain();
  provider.on("display_uri", onDisplayUri);
  try {
    await provider.connect({
      namespaces: {
        solana: {
          chains: [chain],
          methods: ["solana_signTransaction", "solana_signMessage"],
          events: ["accountsChanged"],
        },
      },
    });
  } finally {
    provider.removeListener("display_uri", onDisplayUri);
  }

  if (provider.session) {
    const address = extractSolanaAddress(provider.session);
    if (address) {
      walletAccounts = [buildAccount(address)];
      notifyAccountsChanged();
    }
  }

  await registerWallet();
}

/**
 * Restore a previously established WalletConnect Solana session from IndexedDB.
 * Called on page load to enable auto-reconnect without showing a QR code.
 * Returns true if a valid session was found and restored.
 */
export async function restoreWcSolanaSession(projectId: string): Promise<boolean> {
  try {
    const provider = await getOrInitProvider(projectId);
    if (!provider.session) return false;

    const address = extractSolanaAddress(provider.session);
    if (!address) return false;

    walletAccounts = [buildAccount(address)];
    notifyAccountsChanged();
    void registerWallet();
    return true;
  } catch (error) {
    Logger.error(error, { message: "Failed to restore WalletConnect Solana session" });
    return false;
  }
}

/** Disconnect the WalletConnect Solana session, reset local state, and unregister the wallet. */
export function disconnectWcSolana(): void {
  const provider = wcSolProvider;
  if (!provider) return;

  wcSolProvider = null;
  initPromise = null;
  walletAccounts = [];
  notifyAccountsChanged();
  unregister();

  // universal-provider throws "Please call connect() before enable()" when there is
  // no active session. Auto-restore initializes the provider even when no session
  // exists, so a user-clicked disconnect would throw. Local state is already torn
  // down above; nothing to ask the relay to release.
  if (!provider.session) return;

  disconnecting = provider
    .disconnect()
    .catch((e: unknown) => {
      Logger.error(e, { message: "Failed to disconnect WalletConnect Solana" });
    })
    .finally(() => {
      disconnecting = null;
    });
}

/** Clear Solana-specific WC localStorage keys and IndexedDB as a recovery mechanism. */
function clearWcSolanaLocalStorage(): void {
  if (typeof window === "undefined") return;
  for (const key of Object.keys(localStorage)) {
    if (key.includes("wc-solana")) {
      localStorage.removeItem(key);
    }
  }
  try {
    indexedDB.deleteDatabase("wc-solana");
  } catch {
    // Ignore — IndexedDB may not be available
  }
}
